import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from '@components/atoms/typography';
import { ClockIcon } from '@components/icons/clock-icon';
import { ResponsiveTooltip } from '@components/organisms/tooltips/responsive-tooltip';
import { DocsVerificationStepEnum } from '@models/enums';
import type { ThemeProps } from '@models/types';
import type { TimerProps } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/docs-verification-steps/start/timer/types';
import { useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { RootState } from '@store/root-reducer';
import { docsVerificationSlice } from '@store/slices/docs-verification/docs-verification-slice';
import cn from 'classnames';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

import { TimerContainer } from './styles';

dayjs.extend(duration);

const millisecondsInHour = 3600000;
const fiveMinutes = 5 * 60 * 1000;

export { TimerGlobalStyles } from './global-styles';

export const Timer: React.FC<TimerProps> = ({ isHidden, className }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const theme = useTheme<ThemeProps>();
    const matchesMobileSm = useMediaQuery(`(max-width:${theme.breakPoints.sm})`);
    const sdkOptions = useSelector((state: RootState) => state.docsVerification.sdkOptions);
    const [isExpired, setIsExpired] = useState(() => {
        if (sdkOptions?.expiresAt) {
            return dayjs(sdkOptions?.expiresAt).isBefore(dayjs());
        }

        return false;
    });
    const [timeToExpire, setTimeToExpire] = useState('');
    const millisecondsToExpire = useRef(0);
    const timeoutRef = useRef<number | undefined>();

    useEffect(() => {
        if (sdkOptions?.expiresAt) {
            const checkTime = () => {
                if (sdkOptions?.expiresAt) {
                    let milliseconds = dayjs(sdkOptions?.expiresAt).diff(dayjs());

                    if (milliseconds < 0) {
                        milliseconds = 0;
                    }
                    millisecondsToExpire.current = milliseconds;
                    const durationTime = dayjs.duration(milliseconds);
                    const hours = Math.floor(milliseconds / millisecondsInHour);

                    setTimeToExpire(
                        `${hours ? `${String(hours).padStart(2, '0')}:` : ''}${durationTime.format(
                            'mm:ss',
                        )}`,
                    );
                    if (milliseconds === 0) {
                        setIsExpired(true);
                        dispatch(
                            docsVerificationSlice.actions.setCurrentStep(
                                DocsVerificationStepEnum.EXPIRED,
                            ),
                        );
                    } else {
                        // @ts-expect-error timeout returns number
                        timeoutRef.current = setTimeout(() => {
                            checkTime();
                        }, 1000);
                    }
                }
            };

            checkTime();
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [sdkOptions?.expiresAt, dispatch]);

    if (!sdkOptions?.expiresAt) {
        return <React.Fragment />;
    }

    if (isHidden && millisecondsToExpire.current > fiveMinutes) {
        return <React.Fragment />;
    }

    return (
        <TimerContainer className={className}>
            <ResponsiveTooltip
                title={t('docVerificationWidget.expired.tooltip')}
                classes={{
                    popper: 'docs-ver-expired-tooltip',
                }}
            >
                <Typography
                    tag='div'
                    variant='h2'
                    className={cn('time', {
                        expired: isExpired || millisecondsToExpire.current < fiveMinutes,
                    })}
                >
                    <ClockIcon />
                    <span>
                        {matchesMobileSm
                            ? t('docVerificationWidget.expired.timeLeft', { time: timeToExpire })
                            : timeToExpire}
                    </span>
                </Typography>
            </ResponsiveTooltip>
        </TimerContainer>
    );
};
